import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { listAnalyses, deleteAnalysis } from "@/lib/api";
import { Plus, ArrowRight, FilmStrip, Warning, CheckCircle, Spinner, Trash, ChartBar, Printer, CreditCard, EnvelopeSimple } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import InstallLinkCard from "@/components/InstallLinkCard";
import { api } from "@/lib/api";

const RISK_STYLES = {
  low:      { label: "LOW",      dot: "bg-hazard-safe",     text: "text-hazard-safe" },
  medium:   { label: "MEDIUM",   dot: "bg-hazard-warning",  text: "text-hazard-warning" },
  high:     { label: "HIGH",     dot: "bg-hazard-critical", text: "text-hazard-critical" },
  critical: { label: "CRITICAL", dot: "bg-hazard-critical", text: "text-hazard-critical" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifPrefs, setNotifPrefs] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/me/notifications"); setNotifPrefs(data); }
      catch (e) { /* silent — pill just hides */ }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listAnalyses();
      setAnalyses(data);
    } catch (e) {
      toast.error("Failed to load analyses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      // Polling for processing items
      setAnalyses((curr) => {
        if (curr.some((a) => a.status === "processing")) load();
        return curr;
      });
    }, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this analysis? This cannot be undone.")) return;
    try {
      await deleteAnalysis(id);
      setAnalyses((curr) => curr.filter((a) => a.id !== id));
      toast.success("Analysis deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const completedCount = analyses.filter((a) => a.status === "complete").length;
  const processingCount = analyses.filter((a) => a.status === "processing").length;
  const criticalCount = analyses.filter((a) => a.result?.risk_level === "critical" || a.result?.risk_level === "high").length;

  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="app" />
      <Toaster richColors position="top-right" />

      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-2">Dashboard</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-ink-900">
              {user ? `${user.name?.split(" ")[0] || "Welcome"} — your case log` : "Your case log"}
            </h1>
          </div>
          <Link to="/dashboard/upload">
            <Button data-testid="dashboard-new-analysis" size="lg" className="rounded-none bg-safety hover:bg-safety-hover text-white h-12 px-6 text-sm font-semibold">
              <Plus size={16} weight="bold" className="mr-2" /> New analysis
            </Button>
          </Link>
        </div>

        {/* Secondary actions */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Link to="/dashboard/report">
            <Button variant="outline" size="sm" data-testid="dashboard-daily-report" className="rounded-none border-ink-100 hover:border-ink-900">
              <Printer size={14} className="mr-1.5" weight="bold" /> Daily report
            </Button>
          </Link>
          <Link to="/dashboard/billing">
            <Button variant="outline" size="sm" data-testid="dashboard-billing" className="rounded-none border-ink-100 hover:border-ink-900">
              <CreditCard size={14} className="mr-1.5" weight="bold" /> Billing
            </Button>
          </Link>
          {notifPrefs && (
            <Link to="/dashboard/settings" data-testid="dashboard-notif-pill">
              <span className={`inline-flex items-center gap-2 px-3 h-9 border ${notifPrefs.daily_email ? "border-safety bg-safety/5 text-ink-900" : "border-ink-100 text-ink-500"} hover:border-ink-900 transition-colors font-mono text-[10px] uppercase tracking-[0.25em]`}>
                <EnvelopeSimple size={12} weight="bold" className={notifPrefs.daily_email ? "text-safety" : "text-ink-500"} />
                Daily email
                <span className={notifPrefs.daily_email ? "text-safety" : "text-ink-500"}>·</span>
                {notifPrefs.daily_email ? (
                  <span className="text-ink-900">ON · {formatHour(notifPrefs.delivery_hour)} {tzShort(notifPrefs.timezone)}</span>
                ) : (
                  <span className="text-ink-500">OFF</span>
                )}
              </span>
            </Link>
          )}
        </div>

        {/* Stats row */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-0 border border-ink-100">
          <StatCell label="Total analyses" value={analyses.length} icon={<FilmStrip size={18} weight="bold" />} />
          <StatCell label="Complete" value={completedCount} icon={<CheckCircle size={18} weight="bold" className="text-hazard-safe" />} border />
          <StatCell label="Processing" value={processingCount} icon={<Spinner size={18} weight="bold" className={processingCount ? "text-safety animate-spin" : "text-ink-500"} />} border />
          <StatCell label="High / critical" value={criticalCount} icon={<Warning size={18} weight="bold" className="text-hazard-critical" />} border />
        </div>

        {/* Install / share-to-phone card */}
        <div className="mt-6">
          <InstallLinkCard />
        </div>

        {/* List */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Recent · {analyses.length}</div>
          </div>

          {loading ? (
            <div className="border border-ink-100 p-16 text-center text-ink-500 font-mono text-xs uppercase tracking-[0.3em]">Loading…</div>
          ) : analyses.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="border border-ink-100 divide-y divide-ink-100">
              {analyses.map((a) => (
                <AnalysisRow key={a.id} a={a} onDelete={() => handleDelete(a.id)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, icon, border }) {
  return (
    <div className={`p-6 ${border ? "md:border-l border-ink-100" : ""} bg-white`}>
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">{label}</div>
        <div>{icon}</div>
      </div>
      <div className="mt-4 font-display font-black text-4xl tracking-tighter text-ink-900">{value}</div>
    </div>
  );
}

function formatHour(h) {
  const n = Math.max(0, Math.min(23, Number(h) || 0));
  if (n === 0) return "12 AM";
  if (n < 12) return `${n} AM`;
  if (n === 12) return "12 PM";
  return `${n - 12} PM`;
}

function tzShort(tz) {
  if (!tz) return "";
  if (tz === "UTC") return "UTC";
  const seg = tz.split("/").pop() || tz;
  return seg.replace(/_/g, " ");
}

function AnalysisRow({ a, onDelete }) {
  const r = a.result;
  const style = r ? RISK_STYLES[r.risk_level] : null;
  return (
    <div className="p-5 grid grid-cols-12 gap-4 items-center hover:bg-ink-50 transition-colors" data-testid={`analysis-row-${a.id}`}>
      <div className="col-span-12 md:col-span-5 flex items-start gap-3 min-w-0">
        <div className="w-10 h-10 bg-ink-900 flex items-center justify-center shrink-0">
          <FilmStrip size={18} weight="bold" className="text-white" />
        </div>
        <div className="min-w-0">
          <div className="font-display font-semibold text-ink-900 truncate">{a.title}</div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500 truncate">
            {a.industry} · {a.task_type} · {a.duration_minutes}min/shift
          </div>
        </div>
      </div>
      <div className="col-span-6 md:col-span-3">
        {a.status === "processing" && (
          <div className="inline-flex items-center gap-2 px-2 py-1 border border-safety/40 bg-safety/5 text-safety font-mono text-[10px] uppercase tracking-[0.25em]">
            <Spinner size={12} weight="bold" className="animate-spin" /> Analyzing
          </div>
        )}
        {a.status === "failed" && (
          <div className="inline-flex items-center gap-2 px-2 py-1 border border-hazard-critical/40 bg-hazard-critical/5 text-hazard-critical font-mono text-[10px] uppercase tracking-[0.25em]">
            <Warning size={12} weight="bold" /> Failed
          </div>
        )}
        {a.status === "complete" && style && (
          <div className="flex items-center gap-3">
            <span className={`w-2 h-2 ${style.dot}`} />
            <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${style.text}`}>{style.label} RISK</span>
            <span className="font-display font-bold text-ink-900">{r.overall_risk_score}/100</span>
          </div>
        )}
      </div>
      <div className="col-span-6 md:col-span-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">
        {new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
      </div>
      <div className="col-span-12 md:col-span-2 flex items-center justify-end gap-2">
        <Link to={`/dashboard/analysis/${a.id}`}>
          <Button data-testid={`view-${a.id}`} variant="outline" size="sm" className="rounded-none border-ink-100 hover:border-ink-900">
            Open <ArrowRight size={14} weight="bold" className="ml-1" />
          </Button>
        </Link>
        <Button data-testid={`delete-${a.id}`} variant="ghost" size="sm" onClick={onDelete} className="rounded-none text-ink-500 hover:text-hazard-critical">
          <Trash size={14} weight="bold" />
        </Button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-ink-100 p-16 text-center bg-ink-50">
      <div className="inline-flex items-center justify-center w-12 h-12 bg-ink-900 mb-4">
        <ChartBar size={20} weight="bold" className="text-white" />
      </div>
      <div className="font-display font-bold text-2xl tracking-tight text-ink-900">No analyses yet</div>
      <p className="mt-2 text-ink-500 max-w-md mx-auto">Record any repetitive task — a pick, a lift, a reach — and upload it. SafeForm.ai returns a hazard report and a suggested safer technique for a person to review.</p>
      <Link to="/dashboard/upload">
        <Button data-testid="empty-new-analysis" className="mt-6 rounded-none bg-safety hover:bg-safety-hover text-white" size="lg">
          <Plus size={16} weight="bold" className="mr-2" /> Upload your first video
        </Button>
      </Link>
    </div>
  );
}
